import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Button, Stack, Tabs, Typography, useSnackbar } from '@lyearn/ui';
import useUser from '@/components/Form/components/Conditions/hooks/useUser';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { TimeLogEntityTypeEnum } from '@/types/schema';

import { useUpdateTimeLogMutation } from './graphql/mutations/generated/updateTimeLog';
import { useTimer } from './hooks/useTimer';
import { EntriesView } from './tabs/EntriesView';
import { IntervalView } from './tabs/IntervalView';
import { getTimerMenuTabs, TimerMenuTab } from './utils';

type Props = {
  taskId: string;
  onClose: () => void;
  projectId: string;
};

const TimerMenu = ({ taskId, onClose, projectId }: Props) => {
  const { t } = useTranslation(['Task']);
  const tabs = useMemo(() => getTimerMenuTabs(t), [t]);
  const [currTab, setCurrTab] = useState<TimerMenuTab>(TimerMenuTab.Interval);
  const onTabChange = (tab: string) => {
    setCurrTab(tab as TimerMenuTab);
  };
  const [{ fetching: fetchingUpdateTimeLog }, updateTimeLog] = useUpdateTimeLogMutation();
  const [instructions, setInstructions] = useState<string>('');
  const { id: currentUserId } = useUserContext();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(currentUserId);
  const { user, fetching } = useUser(selectedUserId, !selectedUserId?.length);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const {
    rawTimeInput,
    setRawTimeInput,
    startTime,
    endTime,
    hours,
    minutes,
    handleStartDateChange,
    handleEndDateChange,
    handleStartTimeSelect,
    handleEndTimeSelect,
  } = useTimer();

  const onAddEntry = useCallback(() => {
    if (!selectedUserId) return;
    updateTimeLog({
      data: {
        addTimeLog: {
          endTime: endTime.toISOString(),
          entityId: taskId,
          entityType: TimeLogEntityTypeEnum.Task,
          instruction: instructions,
          parentEntityId: projectId,
          parentEntityType: TimeLogEntityTypeEnum.File,
          startTime: startTime.toISOString(),
          userId: selectedUserId,
        },
      },
    }).then((res) => {
      if (res?.data?.updateTimeLog?.success) {
        showSnackbar({ message: 'Time Log Entry Added Successfully!' });
        onClose();
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
      }
    });
  }, [
    selectedUserId,
    updateTimeLog,
    endTime,
    taskId,
    instructions,
    projectId,
    startTime,
    showSnackbar,
    onClose,
    showErrorSnackbar,
    t,
  ]);

  const addEntryDisabled = useMemo(
    () =>
      fetchingUpdateTimeLog || !selectedUserId || !rawTimeInput || (hours === 0 && minutes === 0),
    [fetchingUpdateTimeLog, selectedUserId, rawTimeInput, hours, minutes],
  );

  return (
    <Stack className="p-[20px] overflow-y-auto max-h-[500px]" direction="col">
      <Stack direction="col">
        <Stack direction="col" gapY={6}>
          <Typography variant="heading-s">{t('Task:TimerMenu.Title')}</Typography>
          <Typography className="text-text-secondary" variant="body-short-02">
            {t('Task:TimerMenu.Description')}
          </Typography>
        </Stack>
        <Tabs className="mt-[20px]" tabs={tabs} value={currTab} onChange={onTabChange} />

        {currTab === TimerMenuTab.Interval && (
          <IntervalView
            endTime={endTime}
            fetching={fetching}
            handleEndDateChange={handleEndDateChange}
            handleEndTimeSelect={handleEndTimeSelect}
            handleStartDateChange={handleStartDateChange}
            handleStartTimeSelect={handleStartTimeSelect}
            instructions={instructions}
            rawTimeInput={rawTimeInput}
            selectedUserId={selectedUserId}
            setInstructions={setInstructions}
            setRawTimeInput={setRawTimeInput}
            setSelectedUserId={setSelectedUserId}
            startTime={startTime}
            user={user}
          />
        )}
        {currTab === TimerMenuTab.Entries && <EntriesView projectId={projectId} taskId={taskId} />}
      </Stack>
      {currTab === TimerMenuTab.Interval ? (
        <Stack className="mt-16" direction="row" gapX={16}>
          <Button fullWidth size="medium" variant="outlined" onClick={onClose}>
            {t('Task:TimerMenu.CancelButton')}
          </Button>
          <Button
            fullWidth
            disabled={addEntryDisabled}
            loading={fetchingUpdateTimeLog}
            size="medium"
            onClick={onAddEntry}>
            {t('Task:TimerMenu.AddEntryButton')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default React.memo(TimerMenu);
